import { getFullVelocityUrl, getWebSocketSubscription, handleResponse, numberWithCommas } from '@/Helper';
import { alarmApi, configurationApi, eventApi, helpApi } from '@/api';
import { ModalInfo, OffNormalModal } from '@/components/common';
import { SubPermissions, User } from '@/model/AccountModel';
import { AlarmModel, AlarmSoundConfiguration, AlarmSoundInfo, EventSortDirection } from '@/model/AlarmModel';
import { ProfileSetting } from '@/model/ConfigurationModel';
import { StatusSummary } from '@/model/EventModel';
import { HelpFeature } from '@/model/HelpModel';
import { Logger } from '@/model/LoggingModel';
import { SubscriptionType, WebSocketNotification } from '@/model/WebSocketModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { setConfiguration, setSocketLiveEventsResponse, setSocketResponse } from '@/store/common/actions';
import { selectAckAlarmList, selectAlarmList, selectClearedAlarmList, selectStatusSummary, selectViewSettings } from '@/store/common/selectors';
import { setHelpFeatures } from '@/store/help/actions';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, notification, Spin, Typography } from 'antd';
import cx from 'classnames';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { ServiceNoConnectionModal } from '../ServiceNoConnectionModal/ServiceNoConnectionModal';
import styles from './footer.module.scss';

//Avoid creating object style inline, since increases reconciliations
const user: User = getUser();
const alarmSoundConfiguration: AlarmSoundConfiguration = user.alarmSoundConfiguration;
const canViewOffNormalPoints: boolean = User.getSubComponentPermission(user, SubPermissions.Velocity_Off_NormalWindow_Use).allowed;
const canViewControlServiceManager: boolean = User.getSubComponentPermission(
	user,
	SubPermissions.ServiceControlManager_ServiceControlManager_ChangeSettings
).allowed;
const canViewAlarms: boolean = User.getSubComponentPermission(user, SubPermissions.AlarmViewer_AlarmViewer_Use).allowed;
const { showStatusBar, showThreatLevel } = user;

const fullVelocityUrl: string = getFullVelocityUrl();
const beepSound = new Audio(`${fullVelocityUrl}Content/Sounds/WindowsBackground.wav`);
const audioIssueHelpLink: string = 'https://identivdocs.atlassian.net/wiki/x/CwCQeQ';

const handleOnClickAlarms = (): void => {
	const url: string = `${fullVelocityUrl}Events/EventViewer`;
	location.href = url;
};
const isDisplayingAlarmViewer = (): boolean => {
	const alarmsContainer = document.getElementById('alarmsPageContainer') as HTMLDivElement;
	return alarmsContainer ? true : false;
};

const isDisplayingEventViewer = (): boolean => {
	const eventViewerContainer = document.getElementById('eventViewerContainer') as HTMLDivElement;
	return eventViewerContainer ? true : false;
};
const isAlarmSoundEnabled = (): boolean => {
	return (
		(alarmSoundConfiguration.BackgroundPlay || isDisplayingAlarmViewer()) &&
		(!alarmSoundConfiguration.SilenceCustom || !alarmSoundConfiguration.SilenceBeep)
	);
};
const webSocketCloseCodeSuccess: number = 1000;
const webSocketCloseCodeLeaveTab: number = 1001;
const webSocketReconnectMaxAttempts: number = 10;
const noIconStyle: CSSProperties = { width: '17px' };
const { Paragraph } = Typography;

const component: React.FC = () => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();

	const [displayOffNormalModal, setDisplayOffNormalModal] = useState<boolean>(false);
	const [isShowServiceNoConnectionModal, setIsShowServiceNoConnectionModal] = useState<boolean>(false);
	const [digitracOnline, setDigitracOnline] = useState<boolean>(false);
	const [activeAlarmsCounter, setActiveAlarmsCounter] = useState<number>(0);
	const [ackAlarmsCounter, setAckAlarmsCounter] = useState<number>(0);
	const [offNormalCounter, setOffNormalCounter] = useState<number>(0);
	const [lastAlarmDescription, setLastAlarmDescription] = useState<string>('');
	const [threatLevel, setThreatLevel] = useState<string>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const statusSummary: StatusSummary = useStoreSelector<StatusSummary>(selectStatusSummary);
	const realTimeAlarmList: AlarmModel[] = useStoreSelector<AlarmModel[]>(selectAlarmList);
	const realTimeAckAlarmList: AlarmModel[] = useStoreSelector<AlarmModel[]>(selectAckAlarmList);
	const realTimeClearedAlarmList: AlarmModel[] = useStoreSelector<AlarmModel[]>(selectClearedAlarmList);
	const profileSettings: ProfileSetting[] = useStoreSelector<ProfileSetting[]>(selectViewSettings);

	const audioContext = useRef<AudioContext>(undefined);
	const fistTimeLoad = useRef<boolean>(true);
	const breakBeep = useRef<boolean>(false);
	const customSoundLoop = useRef<NodeJS.Timeout>(undefined);
	const beepSoundLoop = useRef<NodeJS.Timeout>(undefined);
	const getFirstAlarmLoop = useRef<NodeJS.Timeout>(undefined);
	const lastActiveId = useRef<number>(-1);
	const incomingAlarm = useRef<boolean>(false);
	const lastMakeDelay = useRef<boolean>(false);
	const alarmPlayingAudioId = useRef<number>(0);
	const reconnectAttemptNumber = useRef<number>(0);
	const reconnectTimeout = useRef<NodeJS.Timeout>(undefined);
	const playWaveTimeout = useRef<NodeJS.Timeout>(undefined);
	const resetTimeout = useRef<NodeJS.Timeout>(undefined);
	const webSocket = useRef<WebSocket | null>(null);
	//#endregion

	useEffect(() => {
		const enrollmentContainer = document.getElementById('enrollmentContainer') as HTMLDivElement;
		const photoCallUpContainer = document.getElementById('photoCollectionsGrid') as HTMLDivElement;
		const statusViewerContainer = document.getElementById('statusViewerContainer') as HTMLDivElement;
		const whosInsideContainer = document.getElementById('whosInsideContainer') as HTMLDivElement;
		const statusDashboardContainer = document.getElementById('statusDashboardContainer') as HTMLDivElement;
		const deviceControlContainer = document.getElementById('deviceControlContainer') as HTMLDivElement;
		const graphicContainer = document.getElementById('graphicPageContainer') as HTMLDivElement;
		const reportingContainer = document.getElementById('reportingContainer') as HTMLDivElement;
		const visionContainer = document.getElementById('visionContainer') as HTMLDivElement;
		const settingsContainer = document.getElementById('settings') as HTMLDivElement;

		const helpSources: HelpFeature[] = [];
		let currentSubscriptionType: SubscriptionType = SubscriptionType.StatusSummary;
		if (isDisplayingAlarmViewer()) {
			helpSources.push(HelpFeature.Alarms);
			currentSubscriptionType |= SubscriptionType.Alarms;
		}

		if (isDisplayingEventViewer()) {
			helpSources.push(HelpFeature.Events);
			currentSubscriptionType |= SubscriptionType.Events;
		}

		if (enrollmentContainer) {
			helpSources.push(HelpFeature.Enrollment);
			helpSources.push(HelpFeature.UnassignedAndGuestCredentials);
		}

		if (reportingContainer) {
			helpSources.push(HelpFeature.Reporting);
		}

		if (visionContainer) {
			helpSources.push(HelpFeature.VelocityVision);
		}

		if (settingsContainer) {
			helpSources.push(HelpFeature.OperatorMenu);
		}

		if (photoCallUpContainer) {
			helpSources.push(HelpFeature.PhotoCallUp);
			currentSubscriptionType |= SubscriptionType.PhotoCallUp;
		}

		if (statusViewerContainer) {
			helpSources.push(HelpFeature.StatusViewer);
			currentSubscriptionType |= SubscriptionType.StatusViewer;
		}

		if (whosInsideContainer) {
			helpSources.push(HelpFeature.WhosInside);
			currentSubscriptionType |= SubscriptionType.WhosInside;
		}

		if (deviceControlContainer) {
			helpSources.push(HelpFeature.DeviceControl);
			helpSources.push(HelpFeature.UnassignedAndGuestCredentials);
			helpSources.push(HelpFeature.CredentialTemplates);
			helpSources.push(HelpFeature.FunctionGroups);
			helpSources.push(HelpFeature.Roles);
			helpSources.push(HelpFeature.NetworkGlobalIoConfiguration);
			helpSources.push(HelpFeature.ReaderControlGroup);
			helpSources.push(HelpFeature.DigitracHolidays);
			helpSources.push(HelpFeature.TimeZones);
			helpSources.push(HelpFeature.Operators);
			helpSources.push(HelpFeature.Ports);
			helpSources.push(HelpFeature.XBoxes);
			helpSources.push(HelpFeature.Controllers);
			helpSources.push(HelpFeature.Doors);
			helpSources.push(HelpFeature.ExpansionInputs);
			helpSources.push(HelpFeature.ExpansionRelays);
			helpSources.push(HelpFeature.Readers);
			helpSources.push(HelpFeature.Inputs);
			helpSources.push(HelpFeature.Relays);
			helpSources.push(HelpFeature.CommandSet);
			helpSources.push(HelpFeature.DoorGroups);
			currentSubscriptionType |= SubscriptionType.DeviceControl;
		}

		if (graphicContainer) {
			helpSources.push(HelpFeature.Graphics);
			currentSubscriptionType |= SubscriptionType.Graphic;
		}

		if (statusDashboardContainer) {
			helpSources.push(HelpFeature.StatusDashboard);
			currentSubscriptionType |= SubscriptionType.AccessSummary;
			currentSubscriptionType |= SubscriptionType.QueuedDownloads;
			currentSubscriptionType |= SubscriptionType.PerformanceStatus;
			currentSubscriptionType |= SubscriptionType.Events;
		}
		setWebSocketBySubscriptionType(currentSubscriptionType);

		helpApi
			.getHelp(helpSources)
			.then(helpResponse => {
				if (!handleResponse(helpResponse)) {
					dispatch(setHelpFeatures(helpResponse.Entity));
				}
			})
			.catch(err => Logger.writeErrorLog(err));

		configurationApi
			.retrieveConfiguration()
			.then(configuration => {
				dispatch(setConfiguration(configuration));
			})
			.catch(err => Logger.writeErrorLog(err));

		eventApi
			.retrieveLiveEventSummary()
			.then(liveEvents => {
				if (!handleResponse(liveEvents)) {
					dispatch(setSocketLiveEventsResponse(liveEvents));
				}
			})
			.catch(err => Logger.writeErrorLog(err))
			.finally(() => {
				setIsLoading(false);
			});
		playFirstAlarm();

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			if (customSoundLoop.current) {
				clearTimeout(customSoundLoop.current);
			}
			if (beepSoundLoop.current) {
				clearTimeout(beepSoundLoop.current);
			}
			if (getFirstAlarmLoop.current) {
				clearTimeout(getFirstAlarmLoop.current);
			}
			if (reconnectTimeout.current) {
				clearTimeout(reconnectTimeout.current);
			}
			if (playWaveTimeout.current) {
				clearTimeout(playWaveTimeout.current);
			}
			if (resetTimeout.current) {
				clearTimeout(resetTimeout.current);
			}
		};
	}, []);

	useEffect(() => {
		if (isAlarmSoundEnabled() && profileSettings && realTimeAlarmList.length) {
			validateAlarmSound(realTimeAlarmList, []);
		}
	}, [realTimeAlarmList]);

	useEffect(() => {
		if (isAlarmSoundEnabled() && profileSettings && realTimeAckAlarmList.length) {
			validateAlarmSound([], realTimeAckAlarmList);
		}
	}, [realTimeAckAlarmList]);

	useEffect(() => {
		if (isAlarmSoundEnabled() && profileSettings && realTimeClearedAlarmList.length) {
			validateAlarmSound([], realTimeClearedAlarmList);
		}
	}, [realTimeClearedAlarmList]);

	useEffect(() => {
		if (statusSummary && statusSummary.IsUpdated) {
			batch(() => {
				if (!statusSummary.SDConnected) {
					showServiceNoConnectionModal(true);
				}
				setDigitracOnline(statusSummary.DTConnected);
				setActiveAlarmsCounter(statusSummary.NumAlarms);
				setAckAlarmsCounter(statusSummary.NumAckAlarms);
				setOffNormalCounter(statusSummary.OffNormal);
				setThreatLevel(statusSummary.ThreatLevelCaption);
				updateTitle(statusSummary.NumAlarms);
				if (showAlarmBanner() && statusSummary.LastAlarmDescription && lastAlarmDescription !== statusSummary.LastAlarmDescription) {
					setLastAlarmDescription(statusSummary.LastAlarmDescription);
					notification['error']({
						message: statusSummary.LastAlarmDescription,
					});
				}
			});
		}
	}, [statusSummary]);

	const validateAlarmSound = (alarmList: AlarmModel[], alarmsToStopSound: AlarmModel[]) => {
		let fullSync = false;
		let stopSound = false;
		let newAlarmToReproduce: Partial<AlarmModel> = undefined;

		if (
			alarmPlayingAudioId.current &&
			alarmsToStopSound.length &&
			alarmsToStopSound.find(x => x.AlarmDetails[0].ActiveAlarmId === alarmPlayingAudioId.current)
		) {
			stopSound = true;
		}

		if (!alarmList.length) {
			if (stopSound) fullSync = true;
		} else {
			const sortDirectionIsDescending: boolean = profileSettings.find(x => x.Name === 'QueueSortDir').SelectedValue;
			if (sortDirectionIsDescending) {
				newAlarmToReproduce = alarmList[alarmList.length - 1];
				stopSound = true;
			} else {
				if (!alarmPlayingAudioId.current) {
					newAlarmToReproduce = alarmList[0];
				} else {
					if (stopSound) fullSync = true;
				}
			}
		}

		if (stopSound) {
			alarmPlayingAudioId.current = 0;
			resetValuesForPlayingIncomingAlarm();
			if (getFirstAlarmLoop.current) clearTimeout(getFirstAlarmLoop.current);
		}

		if (newAlarmToReproduce) {
			getFirstAlarmLoop.current = setTimeout(() => {
				playIncomingAlarm(newAlarmToReproduce);
			}, 1500);
		} else {
			if (fullSync) {
				getFirstAlarmLoop.current = setTimeout(() => {
					playFirstAlarm();
				}, 1500);
			}
		}
	};

	const setWebSocketBySubscriptionType = (subscriptionType: SubscriptionType) => {
		console.log(`setWebSocketBySubscriptionType attempt '${reconnectAttemptNumber.current}' for subscriptionType '${subscriptionType}'`);
		const ws: WebSocket = getWebSocketSubscription(subscriptionType);
		ws.onmessage = (e: MessageEvent) => {
			const notification = JSON.parse(e.data) as WebSocketNotification;
			dispatch(setSocketResponse(notification));
		};
		ws.onerror = (e: Event) => {
			Logger.writeErrorLog(e.toString());
		};
		ws.onopen = (e: Event) => {
			reconnectAttemptNumber.current = 0;
			console.log(`socket opened at: ${new Date(Date.now())}`);
		};
		ws.onclose = (e: CloseEvent) => {
			console.log(`socket closed at: ${new Date(Date.now())}`);
			console.log(`code: ${e.code}`);
			console.log(`reason: ${e.reason}`);
			console.log(`wasClean: ${e.wasClean}`);

			if (
				e.code !== webSocketCloseCodeSuccess &&
				e.code !== webSocketCloseCodeLeaveTab &&
				reconnectAttemptNumber.current <= webSocketReconnectMaxAttempts
			) {
				reconnectTimeout.current = setTimeout(() => {
					Logger.writeInfoLog(
						`WebSocket was closed improperly with the code [${e.code}] with reason [${e.reason}], will retry to reconnect. Attempt [${reconnectAttemptNumber.current}/${webSocketReconnectMaxAttempts}].`
					);
					setWebSocketBySubscriptionType(subscriptionType);
					reconnectAttemptNumber.current += 1;
				}, Math.floor(Math.random() * 1000));
			}
		};
		webSocket.current = ws;
	};

	const handleBeforeUnload = (event: Event) => {
		if (webSocket?.current && !event.cancelable) {
			webSocket.current.close();
		}
	};

	const showAlarmBanner = (): boolean => {
		const input = document.getElementById('ShowAlarmBannerHidden') as HTMLInputElement;

		return input.value === '1';
	};

	const updateTitle = (numAlarms: number) => {
		let appendCount = false;
		let title = document.title;
		const index = title.indexOf('(');

		if (index > 0) {
			title = title.substring(0, index).trim();
			if (numAlarms > 0) {
				appendCount = true;
			}
		} else if (numAlarms > 0) {
			appendCount = true;
		}

		if (appendCount) {
			title = `${title} (${numberWithCommas(numAlarms)})`;
		}

		document.title = title;
	};

	const showServiceNoConnectionModal = (visible: boolean) => setIsShowServiceNoConnectionModal(visible);

	const playFirstAlarm = () => {
		if (isAlarmSoundEnabled()) {
			alarmApi
				.getStoredAlarms(
					{
						PageNumber: 1,
						PageSize: 1,
						SortDirection: EventSortDirection.OperatorPreference,
					},
					false,
					[],
					true
				)
				.then(response => {
					if (!handleResponse(response)) {
						if (response && response.alarmList && response.alarmList.length) {
							playIncomingAlarm(response.alarmList[0]);
						}
					}
				})
				.catch(e => Logger.writeErrorLog(e));
		}
	};

	const playIncomingAlarm = (alarmToPlayAudio: Partial<AlarmModel>) => {
		if (isAlarmSoundEnabled()) {
			resetValuesForPlayingIncomingAlarm();
			let alarmId: number = 0;
			if (alarmToPlayAudio && alarmToPlayAudio.AlarmDetails && alarmToPlayAudio.AlarmDetails.length > 0) {
				alarmId = alarmToPlayAudio?.AlarmDetails[0]?.ActiveAlarmId;
			}
			alarmPlayingAudioId.current = alarmId;
			playSound(alarmToPlayAudio?.AlarmSoundInfo ? alarmToPlayAudio?.AlarmSoundInfo : ({ ActiveId: alarmId } as AlarmSoundInfo));
		}
	};

	const playSound = (alarmSoundInfo: AlarmSoundInfo) => {
		if (alarmSoundInfo?.Data) {
			const contextForLoadFile: AudioContext = new AudioContext();
			const alarmSound: string = atob(alarmSoundInfo.Data);
			const buffer: ArrayBuffer = new ArrayBuffer(alarmSound.length);
			const view: Uint8Array = new Uint8Array(buffer);

			for (let i = 0; i < alarmSound.length; i++) {
				view[i] = alarmSound.charCodeAt(i);
			}

			contextForLoadFile
				.decodeAudioData(buffer)
				.then(decodedAudio => {
					playWave(decodedAudio);
					breakBeep.current = true;
				})
				.catch(e => {
					if (alarmSoundInfo.Data.length === 0 || !alarmSoundConfiguration.MakeDelay) {
						playBeep();
						breakBeep.current = false;
					} else {
						Logger.writeErrorLog(e);
					}
				});
		} else {
			playBeep();
			breakBeep.current = false;
		}
	};

	const playWave = (soundArrayBuffer: AudioBuffer) => {
		resetComponent();
		const source: AudioBufferSourceNode = audioContext.current.createBufferSource();
		source.buffer = soundArrayBuffer;
		source.connect(audioContext.current.destination);
		if (!alarmSoundConfiguration.SilenceCustom) {
			source.start();
		} else {
			source.start();
			source.stop();
		}
		source.onended = () => {
			if (alarmSoundConfiguration.MakeDelay) {
				customSoundLoop.current = setTimeout(() => {
					if (lastActiveId.current != 0) {
						playWave(soundArrayBuffer);
					}
				}, alarmSoundConfiguration.DelaySeconds);
			} else if (!incomingAlarm.current) {
				playBeep();
				breakBeep.current = false;
			} else {
				if (lastMakeDelay.current) {
					resetTimeout.current = setTimeout(() => {
						lastActiveId.current = -1;
						incomingAlarm.current = false;
					}, alarmSoundConfiguration.DelaySeconds);
				} else {
					playBeep();
					breakBeep.current = false;
				}
			}
		};
		if (audioContext.current.state === 'suspended') {
			if (fistTimeLoad.current) {
				fistTimeLoad.current = false;
				displaySoundModalError();

				audioContext.current.resume();
			}
			playWaveTimeout.current = setTimeout(() => {
				playWave(soundArrayBuffer);
			}, 1500);
		}
	};

	const resetComponent = () => {
		if (audioContext.current) audioContext.current.close();
		if (beepSoundLoop.current) clearTimeout(beepSoundLoop.current);
		audioContext.current = new window.AudioContext();
		beepSound.currentTime = 0;
	};

	const playBeep = () => {
		resetComponent();
		if (!alarmSoundConfiguration.SilenceBeep && lastActiveId.current != 0) {
			var promise = beepSound.play();
			if (promise !== undefined) {
				promise
					.then(() => {
						//audio is playing
					})
					.catch(() => {
						if (audioContext.current.state === 'suspended' && fistTimeLoad.current) {
							fistTimeLoad.current = false;
							displaySoundModalError();
						}
					});
			}
		}
		beepSoundLoop.current = setTimeout(() => {
			if (!alarmSoundConfiguration.SilenceBeep && !breakBeep.current) {
				playBeep();
			}
		}, 500);
	};

	const resetValuesForPlayingIncomingAlarm = () => {
		if (customSoundLoop.current) clearTimeout(customSoundLoop.current);
		beepSound.pause();
		incomingAlarm.current = true;
		resetComponent();
	};

	const displaySoundModalError = () => {
		const content = (
			<p>
				<span>{`${_('SoundCannotBePlayed')}. ${_('PleaseFollowInstructions')} `}</span>
				<a id='audioHelpLink' href={audioIssueHelpLink} target='_blank'>
					{_('InOurDocumentation')}
				</a>
				<span> {_('ForEnablingTheSound')}.</span>
			</p>
		);

		ModalInfo({
			content: content,
			onOk: () => null,
			onCancel: () => null,
			okText: _('Ok'),
			width: '40%',
		});
	};

	let content: React.ReactElement = <></>;
	if (showStatusBar) {
		content = !isLoading ? (
			<div className={styles.container}>
				{canViewControlServiceManager && (
					<div id='footerDigitracServiceContainer' className={styles.counterContainer}>
						{!isLoading ? (
							digitracOnline ? (
								<CheckCircleOutlined className={styles.onlineIcon} />
							) : (
								<CloseCircleOutlined className={styles.offlineIcon} />
							)
						) : (
							<span style={noIconStyle} />
						)}
						<div style={{ textAlign: 'left' }} className={cx(styles.footerText, styles.space)}>
							{_('DIGITRACService')}:
							<Spin size='small' spinning={isLoading}>
								{!isLoading ? digitracOnline ? _('Online') : _('Offline') : <span>&nbsp;</span>}
							</Spin>
						</div>
					</div>
				)}
				{canViewAlarms && (
					<>
						<div className={styles.counterContainer}>
							{!isLoading ? (
								<ExclamationCircleOutlined
									className={cx(undefined, {
										[styles.disableActiveAlarmsCounter]: activeAlarmsCounter < 1,
										[styles.activeExclamationIcon]: activeAlarmsCounter > 0,
									})}
								/>
							) : (
								<span style={noIconStyle} />
							)}
							<div style={{ textAlign: 'left' }}>
								<Button id='footerActiveAlarmsButton' type='link' onClick={handleOnClickAlarms}>
									<strong data-leavetab={true} className={styles.space}>
										{_('ActiveAlarms')}:
										<Spin size='small' spinning={isLoading}>
											{!isLoading ? numberWithCommas(activeAlarmsCounter) : <span>&nbsp;</span>}
										</Spin>
									</strong>
								</Button>
							</div>
						</div>
						<div className={styles.counterContainer}>
							{!isLoading ? (
								<ExclamationCircleOutlined
									className={cx(undefined, {
										[styles.disableAckAlarmsCounter]: ackAlarmsCounter < 1,
										[styles.ackExclamationIcon]: ackAlarmsCounter > 0,
									})}
								/>
							) : (
								<span style={noIconStyle} />
							)}
							<div style={{ textAlign: 'left' }}>
								<Button id='footerAckAlarmsButton' type='link' onClick={handleOnClickAlarms}>
									<strong data-leavetab={true} className={styles.space}>
										{_('AckAlarms')}:
										<Spin size='small' spinning={isLoading}>
											{!isLoading ? numberWithCommas(ackAlarmsCounter) : <span>&nbsp;</span>}
										</Spin>
									</strong>
								</Button>
							</div>
						</div>
					</>
				)}
				{canViewOffNormalPoints && (
					<div className={styles.counterContainer} style={{ textAlign: 'left' }}>
						<Button
							id='footerOffNormalButton'
							type='link'
							onClick={() => {
								if (!isLoading) {
									setDisplayOffNormalModal(true);
								}
							}}>
							<strong className={styles.space}>
								{_('OffNormal')}:
								<Spin size='small' spinning={isLoading}>
									{!isLoading ? numberWithCommas(offNormalCounter) : <span>&nbsp;</span>}
								</Spin>
							</strong>
						</Button>
					</div>
				)}
				{showThreatLevel && (
					<div id='footerThreatLevel' className={cx(styles.counterContainer, styles.footerText, styles.space)} style={{ textAlign: 'left' }}>
						{_('ThreatLevel')}:
						<Spin size='small' spinning={isLoading}>
							<Paragraph style={{ maxWidth: '83px' }} ellipsis={{ rows: 1, tooltip: false }} title={threatLevel}>
								{!isLoading ? threatLevel : <span>&nbsp;</span>}
							</Paragraph>
						</Spin>
					</div>
				)}
				{displayOffNormalModal && (
					<OffNormalModal
						onClose={() => {
							setDisplayOffNormalModal(false);
						}}
					/>
				)}
				{isShowServiceNoConnectionModal && <ServiceNoConnectionModal onSetVisible={showServiceNoConnectionModal} />}
			</div>
		) : (
			<div className={styles.container} style={{ gap: '7px' }}>
				<strong style={{ color: '#406080' }}>Loading</strong>
				<Spin size='small' spinning={isLoading} />
			</div>
		);
	}

	return content;
};

const Footer = React.memo(component);

export { Footer };
